/**
 * Bibliothèque de composants (D§5).
 *
 * Règle : **aucun écran ne dessine son propre bouton.** Si un composant manque, on
 * l'ajoute ici ; on ne bricole pas localement. C'est la seule façon d'obtenir une
 * interface cohérente par construction plutôt que par relecture.
 *
 * Convention : chaque fabrique rend un objet `{ el, …méthodes }` plutôt qu'un
 * élément augmenté. Le typage reste lisible et les méthodes se découvrent.
 */

import { easeOutCubic, lerp } from '@/core/easing';
import { formatNumber } from '@/core/format';
import type { FormatNumberOptions, LocaleCode } from '@/core/format';
import { icon } from '../icons';
import type { IconName } from '../icons';
import { DOMAIN, DURATION } from '../tokens';
import type { DomainName } from '../tokens';

import './components.css';

export interface Component<E extends HTMLElement = HTMLElement> {
  readonly el: E;
}

export * from './overlays';
export * from './collections';

/* ── Bouton ─────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonOptions {
  readonly label: string;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly icon?: IconName;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly onClick?: () => void;
}

export interface ButtonHandle extends Component<HTMLButtonElement> {
  setLabel(label: string): void;
  setDisabled(disabled: boolean): void;
  /** Le bouton garde sa taille : aucun saut de mise en page pendant l'attente. */
  setLoading(loading: boolean): void;
}

export function createButton(options: ButtonOptions): ButtonHandle {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = `v-btn v-btn--${options.variant ?? 'primary'}`;

  const size = options.size ?? 'md';
  if (size !== 'md') {
    el.classList.add(`v-btn--${size}`);
  }

  const content = document.createElement('span');
  content.className = 'v-btn__content';

  if (options.icon !== undefined) {
    content.append(icon(options.icon));
  }

  const label = document.createElement('span');
  label.textContent = options.label;
  content.append(label);

  const spinner = document.createElement('span');
  spinner.className = 'v-btn__spinner';

  el.append(content, spinner);

  if (options.onClick !== undefined) {
    el.addEventListener('click', options.onClick);
  }

  const handle: ButtonHandle = {
    el,
    setLabel(next) {
      label.textContent = next;
    },
    setDisabled(disabled) {
      el.disabled = disabled;
    },
    setLoading(loading) {
      el.classList.toggle('is-loading', loading);
      el.disabled = loading || el.dataset.userDisabled === 'true';
      el.setAttribute('aria-busy', String(loading));
    },
  };

  if (options.disabled === true) {
    el.dataset.userDisabled = 'true';
    handle.setDisabled(true);
  }
  if (options.loading === true) {
    handle.setLoading(true);
  }

  return handle;
}

/* ── Bouton icône ───────────────────────────────────────────────────────── */

export interface IconButtonOptions {
  readonly icon: IconName;
  /** Obligatoire : un bouton sans texte doit dire ce qu'il fait aux lecteurs d'écran. */
  readonly label: string;
  readonly disabled?: boolean;
  readonly onClick?: () => void;
}

export function createIconButton(options: IconButtonOptions): Component<HTMLButtonElement> {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'v-icon-btn';
  el.setAttribute('aria-label', options.label);
  el.title = options.label;
  el.disabled = options.disabled ?? false;
  el.append(icon(options.icon));

  if (options.onClick !== undefined) {
    el.addEventListener('click', options.onClick);
  }

  return { el };
}

/* ── Panneau ────────────────────────────────────────────────────────────── */

export type Elevation = 'rest' | 'raised' | 'floating';

export interface PanelOptions {
  readonly elevation?: Elevation;
  readonly children?: readonly (Node | string)[];
}

export function createPanel(options: PanelOptions = {}): Component {
  const el = document.createElement('div');
  el.className = `v-panel v-panel--${options.elevation ?? 'rest'}`;
  el.append(...(options.children ?? []));
  return { el };
}

/* ── Carte de bâtiment ──────────────────────────────────────────────────── */

export type CardState = 'affordable' | 'locked' | 'selected' | 'neutral';

export interface CardOptions {
  readonly name: string;
  /** Pictogramme de repli, utilisé tant qu'aucune image n'est fournie. */
  readonly icon: IconName;
  /**
   * Vignette du bâtiment réel, rendue depuis la 3D (`npm run render:sprites`).
   *
   * Une carte qui montre le bâtiment lui-même vaut mieux qu'un pictogramme : le joueur
   * reconnaît dans la boutique exactement ce qui apparaîtra sur sa ville, et le choix
   * se fait au coup d'œil au lieu de la lecture.
   */
  readonly image?: string;
  readonly production: string;
  readonly price: string;
  readonly owned?: number;
  /**
   * Libellé du badge « possédés ».
   *
   * ⚠️ Fourni par l'appelant, jamais écrit ici : le texte était en dur, en français, dans
   * ce composant — ce que la règle 9 interdit, et que `check:i18n` ne pouvait pas voir
   * puisqu'il ne compare que les dictionnaires entre eux. Un joueur anglophone lisait
   * « 3 possédés ».
   */
  readonly ownedLabel?: string;
  /**
   * Pictogramme du prix. `coin` par défaut.
   *
   * ⚠️ Il était CODÉ EN DUR sur la pièce. Les décorations, qui se paient en gemmes depuis
   * la 0.62.0, affichaient donc leur prix sous une icône de pièce — deux monnaies dans un
   * jeu qui en compte exactement deux, et la mauvaise des deux à l'écran.
   */
  readonly priceIcon?: IconName;
  /**
   * Emprise au sol (« 2×2 ») : le joueur doit savoir AVANT de choisir combien de cases
   * le bâtiment mangera — le découvrir au fantôme est une déception évitable.
   */
  readonly footprint?: string;
  readonly state?: CardState;
  readonly onClick?: () => void;
}

export interface CardHandle extends Component<HTMLButtonElement> {
  setState(state: CardState): void;
  /**
   * Ligne d'indice sous le prix — « prêt dans 1 min 40 » sur une carte trop chère.
   * `null` la retire : un indice vide laisserait un trou dans la colonne.
   */
  setHint(text: string | null): void;
  /**
   * Réécrit la ligne de production.
   *
   * Les bâtiments l'ont fixe — leur production de base ne change pas. Un point de repère,
   * si : ce qu'il ajoute dépend de ce que la ville produit déjà (`LANDMARK_SHARE`). Sans
   * ce point d'entrée, sa carte annonçait une règle approximative au lieu du gain réel.
   */
  setProduction(text: string): void;
}

const CARD_STATE_CLASSES: Record<CardState, string | null> = {
  affordable: 'v-card--affordable',
  locked: 'v-card--locked',
  selected: 'v-card--selected',
  neutral: null,
};

export function createCard(options: CardOptions): CardHandle {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'v-card';

  const thumb = document.createElement('span');
  thumb.className = 'v-card__thumb';

  if (options.image === undefined) {
    thumb.append(icon(options.icon, { size: 'large' }));
  } else {
    const img = document.createElement('img');
    img.className = 'v-card__image';
    img.src = options.image;
    img.alt = '';
    // Décoratif : le nom du bâtiment est juste à côté, le répéter serait du bruit
    // pour un lecteur d'écran.
    img.setAttribute('aria-hidden', 'true');
    // Chargement paresseux : dix vignettes au premier rendu retarderaient l'affichage
    // de la boutique pour des cartes que le joueur n'a pas encore fait défiler.
    img.loading = 'lazy';
    img.decoding = 'async';
    thumb.append(img);
    thumb.classList.add('v-card__thumb--image');
  }

  // Nommé, et pas laissé anonyme : la mise en page compacte doit pouvoir le recentrer
  // sans passer par un `nth-child`, qui se tromperait au premier enfant ajouté.
  const middle = document.createElement('span');
  middle.className = 'v-card__body';

  const name = document.createElement('span');
  name.className = 'v-card__name';
  name.textContent = options.name;

  const meta = document.createElement('span');
  meta.className = 'v-card__meta';
  meta.append(icon('coin'), options.production);

  middle.append(name, meta);

  if (options.footprint !== undefined) {
    const footprint = document.createElement('span');
    footprint.className = 'v-card__footprint';
    footprint.textContent = options.footprint;
    middle.append(footprint);
  }

  const right = document.createElement('span');
  right.className = 'v-card__right';

  const price = document.createElement('span');
  price.className = 'v-card__price';
  price.append(icon(options.priceIcon ?? 'coin'), options.price);
  right.append(price);

  const hint = document.createElement('span');
  hint.className = 'v-card__hint';
  hint.hidden = true;
  right.append(hint);

  if (options.owned !== undefined && options.ownedLabel !== undefined) {
    right.append(createBadge({ text: options.ownedLabel, domain: 'coins', subtle: true }).el);
  }

  el.append(thumb, middle, right);

  if (options.onClick !== undefined) {
    el.addEventListener('click', options.onClick);
  }

  const setProduction = (text: string): void => {
    /*
      `meta` contient une icône PUIS le texte : on ne réécrit que le dernier nœud, sinon
      l'icône disparaîtrait au premier rafraîchissement — dix fois par seconde.
    */
    const last = meta.lastChild;
    if (last !== null) last.textContent = text;
  };

  const setState = (state: CardState): void => {
    for (const className of Object.values(CARD_STATE_CLASSES)) {
      if (className !== null) {
        el.classList.remove(className);
      }
    }
    const next = CARD_STATE_CLASSES[state];
    if (next !== null) {
      el.classList.add(next);
    }
    el.disabled = state === 'locked';
  };

  setState(options.state ?? 'neutral');

  return {
    el,
    setState,
    setProduction,
    setHint(text) {
      hint.hidden = text === null;
      if (text !== null) hint.textContent = text;
    },
  };
}

/* ── Badge ──────────────────────────────────────────────────────────────── */

export interface BadgeOptions {
  readonly text: string;
  readonly domain?: DomainName;
  readonly icon?: IconName;
  /** Version discrète, sur fond clair (compteur « possédés » d'une carte). */
  readonly subtle?: boolean;
}

export function createBadge(options: BadgeOptions): Component<HTMLSpanElement> {
  const el = document.createElement('span');
  el.className = 'v-badge';

  if (options.subtle === true) {
    el.style.background = 'rgb(43 36 22 / 8%)';
    el.style.color = 'var(--text-on-cream-soft)';
  } else if (options.domain !== undefined) {
    const tone = DOMAIN[options.domain].base;
    el.style.color = tone;
    el.style.background = `color-mix(in srgb, ${tone} 18%, transparent)`;
    el.style.borderColor = `color-mix(in srgb, ${tone} 45%, transparent)`;
  }

  if (options.icon !== undefined) {
    const glyph = icon(options.icon);
    glyph.style.width = '13px';
    glyph.style.height = '13px';
    el.append(glyph);
  }

  el.append(options.text);
  return { el };
}

/* ── Barre de progression ───────────────────────────────────────────────── */

export interface ProgressOptions {
  readonly value: number;
  readonly max?: number;
  readonly domain?: DomainName;
  readonly label?: string;
}

export interface ProgressHandle extends Component {
  setValue(value: number): void;
}

export function createProgress(options: ProgressOptions): ProgressHandle {
  const max = options.max ?? 100;
  const el = document.createElement('div');
  el.className = 'v-progress';
  el.setAttribute('role', 'progressbar');
  el.setAttribute('aria-valuemin', '0');
  el.setAttribute('aria-valuemax', String(max));
  if (options.label !== undefined) {
    el.setAttribute('aria-label', options.label);
  }

  const fill = document.createElement('i');
  fill.className = 'v-progress__fill';

  const tone = DOMAIN[options.domain ?? 'social'];
  fill.style.background = `linear-gradient(90deg, ${tone.deep}, ${tone.base})`;
  fill.style.boxShadow = `0 0 14px color-mix(in srgb, ${tone.base} 55%, transparent)`;

  el.append(fill);

  const setValue = (value: number): void => {
    const ratio = max <= 0 ? 0 : Math.min(1, Math.max(0, value / max));
    fill.style.width = `${(ratio * 100).toFixed(2)}%`;
    el.setAttribute('aria-valuenow', String(Math.round(value)));
  };

  setValue(options.value);

  return { el, setValue };
}

/* ── Compteur de ressource ──────────────────────────────────────────────── */

export type CounterSize = 'sm' | 'md' | 'lg';

export interface CounterOptions {
  readonly value: number;
  readonly domain: DomainName;
  readonly icon?: IconName;
  readonly size?: CounterSize;
  readonly locale?: LocaleCode;
  readonly format?: FormatNumberOptions;
}

export interface CounterHandle extends Component {
  /** Anime jusqu'à la valeur cible. Un nombre ne saute jamais (E§1.9). */
  setValue(value: number, options?: { readonly animate?: boolean }): void;
  getValue(): number;
  destroy(): void;
}

export function createCounter(options: CounterOptions): CounterHandle {
  const el = document.createElement('span');
  el.className = `v-counter v-counter--${options.size ?? 'md'}`;

  const tone = DOMAIN[options.domain].base;

  if (options.icon !== undefined) {
    el.append(
      icon(options.icon, { size: options.size === 'lg' ? 'large' : 'regular', color: tone }),
    );
  }

  const value = document.createElement('span');
  value.className = 'v-counter__value';
  value.style.color = tone;
  el.append(value);

  const locale = options.locale ?? 'fr';
  let current = options.value;
  let frame = 0;

  const paint = (n: number): void => {
    value.textContent = formatNumber(n, locale, options.format);
  };

  paint(current);

  const setValue: CounterHandle['setValue'] = (target, opts = {}) => {
    if (frame !== 0) {
      cancelAnimationFrame(frame);
      frame = 0;
    }

    if (opts.animate === false || target === current) {
      current = target;
      paint(current);
      return;
    }

    const from = current;
    const start = performance.now();

    const step = (now: number): void => {
      const progress = Math.min(1, (now - start) / DURATION.celebration);
      current = lerp(from, target, easeOutCubic(progress));
      paint(current);

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        frame = 0;
        current = target;
        paint(current);
        el.classList.add('is-bumping');
        globalThis.setTimeout(() => {
          el.classList.remove('is-bumping');
        }, DURATION.celebration);
      }
    };

    frame = requestAnimationFrame(step);
  };

  return {
    el,
    setValue,
    getValue: () => current,
    destroy() {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    },
  };
}

/* ── État vide ──────────────────────────────────────────────────────────── */

export interface EmptyStateOptions {
  readonly icon: IconName;
  readonly title: string;
  readonly text: string;
  readonly action?: ButtonOptions;
}

export function createEmptyState(options: EmptyStateOptions): Component {
  const el = document.createElement('div');
  el.className = 'v-empty';

  const crest = document.createElement('div');
  crest.className = 'v-empty__crest';
  crest.append(icon(options.icon, { size: 'large' }));

  const title = document.createElement('p');
  title.className = 'v-empty__title';
  title.textContent = options.title;

  const text = document.createElement('p');
  text.className = 'v-empty__text';
  text.textContent = options.text;

  el.append(crest, title, text);

  if (options.action !== undefined) {
    el.append(createButton(options.action).el);
  }

  return { el };
}

/* ── Squelette de chargement ────────────────────────────────────────────── */

export interface SkeletonOptions {
  readonly height: number;
  readonly width?: string;
}

export function createSkeleton(options: SkeletonOptions): Component {
  const el = document.createElement('div');
  el.className = 'v-skeleton';
  el.style.height = `${options.height}px`;
  el.style.width = options.width ?? '100%';
  el.setAttribute('aria-hidden', 'true');
  return { el };
}
